import { useEffect } from "react";

/**
 * SEOHead — lightweight per-route metadata updater.
 *
 * Sets `document.title`, meta description, canonical link, Open Graph &
 * Twitter cards + a JSON-LD blob so JS-rendering crawlers (Googlebot,
 * Bingbot, Applebot, Discord, Notion, ChatGPT) see the correct page
 * identity. Static crawlers still get the shell defaults baked into
 * `public/index.html`.
 *
 * Usage:
 *   <SEOHead
 *     title="Farmers Pulse — Weather, Crops, Markets & Farm Intelligence"
 *     description="Living Agricultural Intelligence Network for farmers everywhere."
 *     path="/"
 *   />
 */
function upsertMeta(attr, key, value) {
  if (!value) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
}

function upsertLink(rel, href) {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function upsertJsonLd(id, json) {
  let el = document.head.querySelector(`script[data-jsonld="${id}"]`);
  if (!el) {
    el = document.createElement("script");
    el.setAttribute("type", "application/ld+json");
    el.setAttribute("data-jsonld", id);
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(json);
}

export default function SEOHead({
  title,
  description,
  path = "",
  image,
  brand = "FarmersPulse",
  jsonLd,
  robots = "index,follow",
}) {
  useEffect(() => {
    if (title) document.title = title;
    upsertMeta("name", "description", description);
    upsertMeta("name", "robots", robots);

    const origin =
      typeof window !== "undefined"
        ? window.location.origin
        : "https://farmerspulse.com";
    const canonical = origin + (path || (typeof window !== "undefined" ? window.location.pathname : "/"));
    upsertLink("canonical", canonical);

    // OG + Twitter
    upsertMeta("property", "og:site_name", brand);
    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:url", canonical);
    if (image) upsertMeta("property", "og:image", image);
    upsertMeta("name", "twitter:card", image ? "summary_large_image" : "summary");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    if (image) upsertMeta("name", "twitter:image", image);

    if (jsonLd) upsertJsonLd("page", jsonLd);
  }, [title, description, path, image, brand, robots, jsonLd]);

  return null;
}
